/**
 * AppHost — die Naht zwischen einer App und ihrem Wirt (#504).
 *
 * Eine App wie Communicator 2.0 oder Drive 2.0 läuft in zwei Darstellungen: als
 * Fenster im WebOS-Desktop und unter einer shell-freien Route (`/chat`,
 * `/drive`) als Single-Page-App. Alles, was der Fenstermanager kann und eine
 * Route anders löst, läuft über dieses Interface — damit die Unterschiede am
 * Rand bleiben (im Adapter) statt als `if (isStandalone)` in den Komponenten zu
 * landen.
 *
 * Der Default ist bewusst der Standalone-No-op: der Fenster-Hook wirft ohne
 * `WindowsProvider`, `useAppHost` niemals. Eine Komponente, die ohne Provider
 * gerendert wird (Tests, Admin-Toolbox-Tabs), bekommt eine funktionierende,
 * fensterlose Variante statt eines Absturzes.
 *
 * Den Fenster-Adapter stellt `WindowAppHost` (`@/components/system/WindowAppHost`)
 * bereit — bewusst in einer eigenen Datei, damit ein späterer Standalone-Entry
 * (Phase 2: eigenes Build-Ziel) den Fenstermanager nicht über diesen Import
 * wieder ins Bundle zieht.
 */
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import type { AppId } from '@/data/apps'
import type { WindowParams } from '@/types/window'

export interface AppHost {
  /** Wirt der App: eigenes Fenster im Desktop oder shell-freie Route. */
  mode: 'window' | 'standalone'
  /**
   * Identität DIESER App-Instanz, wirt-neutral (#514).
   *
   * Im Desktop die Fenster-ID: derselbe Nutzer kann den Communicator zweimal
   * offen haben, und ein Intent, den das eine Fenster ausgelöst hat, darf nicht
   * im anderen landen. Standalone `undefined` — dort gibt es je Route genau
   * eine Instanz, es ist also nichts zu unterscheiden.
   *
   * Bewusst NICHT `windowId` genannt: Apps dürfen nicht wissen, ob sie in einem
   * Fenster laufen. Wer sie adressiert, vergleicht nur auf Gleichheit und
   * behandelt `undefined` als „an niemanden im Besonderen".
   */
  instanceId?: string
  /** Deep-Link-Parameter — Fenster-Params bzw. URL-Query. */
  params: WindowParams
  /** Aktuellen Zustand in den Deep-Link zurückschreiben (Fenster-Params bzw. URL-Query). */
  setParams: (params: WindowParams) => void
  /** Kontext-Anzeige des Wirts (Fenster-Untertitel bzw. Dokumenttitel). */
  setSubtitle: (subtitle: string) => void
  /**
   * Eine andere App in den Vordergrund holen. Im Fenster: vorhandenes Fenster
   * fokussieren, sonst öffnen. Standalone: zur Route der App navigieren.
   */
  openApp: (appId: AppId, params?: WindowParams) => void
  /**
   * Ob der Wirt einen eigenen Einstellungsort hat, der die App-Einstellungen
   * mit aufnimmt. Trifft das zu, blendet die App ihren eigenen Zugang dorthin
   * aus — sonst führen zwei Wege zur selben Seite. Standalone: die Rail hat
   * `/settings` und bettet sie dort ein. Im Fenster: nein, die App zeigt ihren
   * eigenen Eintrag (die Settings-App des Desktops kennt sie nicht).
   */
  hostsAppSettings: boolean
  /** Ob der Wirt Inhalte in ein eigenes Fenster herauslösen kann. */
  canPopOut: boolean
  /** Inhalt in ein eigenes Fenster herauslösen. No-op, wenn `canPopOut` false ist. */
  popOut: (appId: AppId, title: string, params?: WindowParams) => void
  /**
   * Ob DIESE App eine shell-freie Route hat (`STANDALONE_ROUTES`) UND der
   * angemeldete Nutzer dort zugelassen ist (`pdb` in `allowed_applications`,
   * siehe `STANDALONE_APPLICATION`). Im Fenster: bietet einen Weg aus dem
   * Fenster heraus in die volle Seite. Unter einer shell-freien Route immer
   * `false` — dort gibt es nichts weiter zu öffnen.
   */
  canOpenStandalone: boolean
  /**
   * Die Route dieser App in einem neuen Tab öffnen, mit den aktuellen
   * Deep-Link-Parametern. No-op, wenn `canOpenStandalone` false ist.
   */
  openStandalone: () => void
}

/**
 * Apps, die eine shell-freie Route haben. Einzige Stelle, an der die Zuordnung
 * App → Route steht; `StandaloneLayout` liest dieselbe Tabelle für die Rail.
 * Eine App ohne Eintrag ist im Standalone-Modus schlicht nicht erreichbar
 * (`openApp` bleibt dann wirkungslos, statt ins Leere zu navigieren).
 */
export const STANDALONE_ROUTES: Partial<Record<AppId, string>> = {
  communicator2: '/chat',
  drive2: '/drive',
  harness: '/harness',
}

const STANDALONE_NOOP: AppHost = {
  mode: 'standalone',
  instanceId: undefined,
  params: {},
  setParams: () => {},
  setSubtitle: () => {},
  openApp: () => {},
  hostsAppSettings: false,
  canPopOut: false,
  popOut: () => {},
  canOpenStandalone: false,
  openStandalone: () => {},
}

const AppHostContext = createContext<AppHost>(STANDALONE_NOOP)

export function AppHostProvider({ host, children }: { host: AppHost; children: ReactNode }) {
  return <AppHostContext.Provider value={host}>{children}</AppHostContext.Provider>
}

export function useAppHost(): AppHost {
  return useContext(AppHostContext)
}

/** Query-String → WindowParams. Werte kommen aus der URL immer als String. */
function paramsFromSearch(search: string): WindowParams {
  return Object.fromEntries(new URLSearchParams(search))
}

/** WindowParams → Query-String. `undefined` entfernt den Parameter (Fenster-Semantik).
 *  Exportiert, damit `WindowAppHost` dieselbe Serialisierung für `openStandalone` nutzt. */
export function searchFromParams(params: WindowParams): string {
  const next = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') next.set(key, String(value))
  }
  next.sort()
  return next.toString()
}

/**
 * Einen Patch auf die bestehende Query anwenden — genau wie `updateWindowParams`
 * im Fenster-Wirt (#698).
 *
 * Bis dahin ERSETZTE der Standalone-Wirt die Query komplett, der Fenster-Wirt
 * patchte: dieselbe App schrieb also je nach Wirt etwas anderes. Der Unterschied
 * war nicht theoretisch — der Drive löschte beim Schreiben seiner Knowledge Area
 * das `documentId` eines geteilten Links wieder weg (#516), und der Communicator
 * hätte `conversationId` gegen `view` verloren.
 *
 * `undefined` löscht weiterhin einen Schlüssel; das ist die Fenster-Semantik und
 * der einzige Weg, einen Parameter unter Patch-Regeln wieder loszuwerden.
 */
function patchSearch(search: string, patch: WindowParams): string {
  const next = new URLSearchParams(search)
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined || value === '') next.delete(key)
    else next.set(key, String(value))
  }
  next.sort()
  return next.toString()
}

/**
 * Host für die shell-freien Routen: Deep-Link-Parameter stehen in der URL-Query
 * (`/chat?view=routines`), der Kontext landet im Dokumenttitel, App-Wechsel ist
 * Navigation, und herauslösen gibt es ohne Fenstermanager nicht.
 */
export function useStandaloneAppHost(baseTitle: string): AppHost {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  // Query-String als Identität, nicht das SearchParams-Objekt: react-router
  // liefert bei jedem Render eine neue Instanz, der Host wäre sonst nie stabil.
  const search = searchParams.toString()

  const setParams = useCallback(
    (params: WindowParams) => {
      // Patch statt Ersetzen (#698): eine App schreibt einen Parameter fort,
      // ohne die der anderen mitzunehmen — dieselbe Bedeutung wie im Fenster.
      const next = patchSearch(search, params)
      // Nur schreiben, wenn sich wirklich etwas ändert — Apps rufen das aus
      // einem Effekt heraus auf, und ein bedingungsloses Schreiben würde sich
      // über den neuen Query-String selbst erneut auslösen. Der Vergleich läuft
      // gegen das GEPATCHTE Ergebnis; gegen die Eingabe verglichen würde jedes
      // Teil-Update als Änderung gelten und der Effekt sich selbst nachziehen.
      const current = new URLSearchParams(search)
      current.sort()
      if (current.toString() === next) return
      // `replace`, damit ein Wechsel der Knowledge Area keine History-Einträge
      // stapelt — der Zurück-Knopf soll die App verlassen, nicht durch Filter gehen.
      setSearchParams(next, { replace: true })
    },
    [search, setSearchParams],
  )

  return useMemo(
    () => ({
      mode: 'standalone',
      // Eine Instanz je Route — nichts zu unterscheiden (#514).
      instanceId: undefined,
      params: paramsFromSearch(search),
      setParams,
      setSubtitle: (subtitle: string) => {
        document.title = subtitle ? `${baseTitle} — ${subtitle}` : baseTitle
      },
      openApp: (appId: AppId, params?: WindowParams) => {
        const route = STANDALONE_ROUTES[appId]
        if (!route) return
        const query = params ? searchFromParams(params) : ''
        navigate(query ? `${route}?${query}` : route)
      },
      // `/settings` bettet die App-Einstellungen ein.
      hostsAppSettings: true,
      canPopOut: false,
      popOut: () => {},
      // Schon standalone — nichts weiter zu öffnen.
      canOpenStandalone: false,
      openStandalone: () => {},
    }),
    [search, setParams, baseTitle, navigate],
  )
}
